import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi } from "./adminApi";

const EVENT_LABELS = {
    landed: "Landed",
    assessment_started: "Assessment started",
    assessment_completed: "Assessment completed",
    archetype_viewed: "Archetype viewed",
    archetype_shared: "Archetype shared",
    waitlist_joined: "Waitlist joined",
    founding_purchased: "Founding purchased",
};

export default function AdminFunnel() {
    const [data, setData] = useState(null);
    const [threshold, setThreshold] = useState("");
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        const d = await adminApi.funnel();
        setData(d);
        setThreshold(String(d.market_open_threshold));
    }, []);

    useEffect(() => { load(); }, [load]);

    const saveThreshold = async () => {
        const n = parseInt(threshold, 10);
        if (!n || n < 1) { toast.error("Enter a number of at least 1"); return; }
        setSaving(true);
        try {
            await adminApi.setFunnelThreshold(n);
            toast.success("Opening threshold updated");
            await load();
        } catch {
            toast.error("Could not save threshold");
        }
        setSaving(false);
    };

    if (!data) return <div className="p-10 text-ink-muted">Loading…</div>;

    const maxFunnel = Math.max(1, ...data.funnel.map(f => f.sessions));
    const maxDrop = Math.max(1, ...data.dropoff_by_question.map(d => d.stalled));
    const maxArch = Math.max(1, ...data.archetype_distribution.map(a => a.count));

    return (
        <div className="p-10 max-w-5xl space-y-10" data-testid="admin-funnel">
            <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                    <span className="text-[10px] tracking-[0.36em] uppercase text-gold-deep">Growth</span>
                    <h1 className="mt-2 font-display text-5xl text-burgundy"><span className="italic">Funnel</span> &amp; demand</h1>
                </div>
                <a
                    data-testid="funnel-export-csv"
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/funnel/export.csv`}
                    className="border border-burgundy text-burgundy uppercase text-[10px] tracking-[0.24em] px-4 py-2.5"
                >
                    Export CSV
                </a>
            </div>

            {/* Funnel */}
            <Section title="Event funnel">
                <div className="space-y-2">
                    {data.funnel.map(f => (
                        <div key={f.event} className="flex items-center gap-3" data-testid={`funnel-row-${f.event}`}>
                            <div className="w-48 text-xs text-ink-muted uppercase tracking-[0.14em]">{EVENT_LABELS[f.event]}</div>
                            <div className="flex-1 bg-burgundy/5 h-6">
                                <div className="h-6 bg-burgundy" style={{ width: `${(f.sessions / maxFunnel) * 100}%` }} />
                            </div>
                            <div className="w-14 text-right font-display text-lg text-burgundy">{f.sessions}</div>
                        </div>
                    ))}
                </div>
            </Section>

            <div className="grid md:grid-cols-2 gap-8">
                <Section title="Assessment completion">
                    <BigStat testid="stat-completion" value={`${data.assessment.completion_rate}%`}
                        sub={`${data.assessment.completed} completed of ${data.assessment.started} started`} />
                    <div className="mt-5">
                        <div className="text-[10px] tracking-[0.24em] uppercase text-ink-muted mb-2">Drop-off by question (stalled at)</div>
                        {data.dropoff_by_question.length === 0 && <div className="text-xs text-ink-light">No abandoned assessments.</div>}
                        <div className="space-y-1.5">
                            {data.dropoff_by_question.map(d => (
                                <div key={d.question} className="flex items-center gap-2 text-xs">
                                    <span className="w-10 text-ink-muted">Q{d.question}</span>
                                    <div className="flex-1 bg-burgundy/5 h-3">
                                        <div className="h-3 bg-gold" style={{ width: `${(d.stalled / maxDrop) * 100}%` }} />
                                    </div>
                                    <span className="w-8 text-right text-ink">{d.stalled}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </Section>

                <Section title="Waitlist & Founding">
                    <div className="grid grid-cols-2 gap-4">
                        <BigStat testid="stat-waitlist" value={data.waitlist.total}
                            sub={`+${data.waitlist.joined_last7} last 7d${data.waitlist.growth_rate_pct != null ? ` · ${data.waitlist.growth_rate_pct > 0 ? "+" : ""}${data.waitlist.growth_rate_pct}% wk/wk` : ""}`} />
                        <BigStat testid="stat-founding" value={`${data.founding.claimed}/100`} sub="Founder's Club claimed" />
                    </div>
                    <div className="mt-6 pt-5 border-t border-burgundy/10">
                        <div className="text-[10px] tracking-[0.24em] uppercase text-ink-muted mb-2">Market opening threshold (assessed members)</div>
                        <div className="flex gap-2">
                            <input
                                data-testid="threshold-input"
                                type="number"
                                min="1"
                                value={threshold}
                                onChange={e => setThreshold(e.target.value)}
                                className="w-28 border border-burgundy/25 bg-cream px-3 py-2 text-sm"
                            />
                            <button
                                data-testid="threshold-save-btn"
                                onClick={saveThreshold}
                                disabled={saving}
                                className="bg-burgundy text-cream uppercase text-[10px] tracking-[0.22em] px-4 disabled:opacity-60"
                            >
                                {saving ? "Saving…" : "Save"}
                            </button>
                        </div>
                    </div>
                </Section>

                <Section title="Referrals">
                    <BigStat testid="stat-viral-k" value={data.referrals.viral_coefficient}
                        sub={`viral coefficient · ${data.referrals.referred_signups} referred signups`} />
                    <div className="mt-4">
                        <div className="text-[10px] tracking-[0.24em] uppercase text-ink-muted mb-2">Leaderboard</div>
                        {data.referrals.leaderboard.length === 0 && <div className="text-xs text-ink-light">No referrals yet.</div>}
                        <div className="space-y-1.5">
                            {data.referrals.leaderboard.map((r, i) => (
                                <div key={r.referral_code} className="flex items-center justify-between text-xs" data-testid={`leaderboard-row-${i}`}>
                                    <span className="text-ink">{i + 1}. {r.email || r.referral_code}</span>
                                    <span className="font-display text-base text-burgundy">{r.referred}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </Section>

                <Section title="Archetype distribution">
                    {data.archetype_distribution.length === 0 && <div className="text-xs text-ink-light">No completed assessments yet.</div>}
                    <div className="space-y-1.5">
                        {data.archetype_distribution.map(a => (
                            <div key={a.archetype_id} className="flex items-center gap-2 text-xs">
                                <span className="w-40 text-ink-muted truncate">{a.archetype_id}</span>
                                <div className="flex-1 bg-burgundy/5 h-3">
                                    <div className="h-3 bg-burgundy" style={{ width: `${(a.count / maxArch) * 100}%` }} />
                                </div>
                                <span className="w-8 text-right text-ink">{a.count}</span>
                            </div>
                        ))}
                    </div>
                </Section>
            </div>

            <Section title="Traffic sources (first touch)">
                {data.traffic_sources.length === 0 && <div className="text-xs text-ink-light">No landed events yet.</div>}
                <div className="grid sm:grid-cols-3 gap-3">
                    {data.traffic_sources.map(s => (
                        <div key={s.source} className="border border-burgundy/15 bg-cream p-4" data-testid={`source-${s.source}`}>
                            <div className="text-[10px] tracking-[0.2em] uppercase text-ink-muted truncate">{s.source}</div>
                            <div className="mt-1 font-display text-2xl text-burgundy">{s.sessions}</div>
                        </div>
                    ))}
                </div>
            </Section>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="border border-burgundy/15 bg-cream-soft p-6">
            <h2 className="text-[10px] tracking-[0.3em] uppercase text-gold-deep mb-4">{title}</h2>
            {children}
        </div>
    );
}

function BigStat({ value, sub, testid }) {
    return (
        <div data-testid={testid}>
            <div className="font-display text-4xl text-burgundy">{value}</div>
            <div className="mt-1 text-xs text-ink-muted">{sub}</div>
        </div>
    );
}
